import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { bugsnag } from 'lib/external/bugsnag';

import {
  useRequestCreateActivity,
  useCreateActivityRequestParams,
} from 'lib/hooks';
import { TEMP_REFS } from 'lib/constants';
import { getPendingActivityTypeRef } from 'lib/selectors/getPendingActivityTypeRef';
import { getActivityTypes } from 'lib/selectors/getActivityTypes';
import { getIsChangeActivitiesLoading } from 'lib/selectors/getIsChangeActivitiesLoading';
import {
  actionCanonicalActivityDelete,
  actionCanonicalActivityCreate,
  actionOptimisticActivityCreate,
  actionSetLaborTicketActionLoading,
  actionSetPendingActivityTypeRef,
  actionSetIsChangeActivitiesLoading,
} from 'lib/actions';
import { getBugsnagUserInfo } from 'lib/selectors/getBugsnagUserInfo';
import { throwErrorToast } from 'lib/utils/toast';

const { ACTIVITY_REF: optimisticActivityRef } = TEMP_REFS;

const useChangeActivities = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const createActivityRequestParams = useCreateActivityRequestParams();
  const bugsnagUserInfo = useSelector(getBugsnagUserInfo);

  const isChangeActivitiesLoading = useSelector(getIsChangeActivitiesLoading);
  const activityTypes = useSelector(getActivityTypes);
  const pendingActivityTypeRef = useSelector(getPendingActivityTypeRef);
  const pendingActivityTypeName = activityTypes.find(({ activityTypeRef }) => {
    return activityTypeRef === pendingActivityTypeRef;
  })?.name;

  const [requestCreateActivity, { loading }] = useRequestCreateActivity({
    update: (
      _cache,
      {
        data: {
          createActivityResponse: { activity },
        },
      }
    ) => {
      dispatch(actionOptimisticActivityCreate({ activity }));
    },
    onCompleted: ({ createActivityResponse: { activity } }) => {
      dispatch(
        actionCanonicalActivityCreate({ activity, optimisticActivityRef })
      );
      dispatch(actionSetPendingActivityTypeRef(null));
    },
    onError: (err) => {
      bugsnag.notify(err, (event) => {
        event.addMetadata('user', bugsnagUserInfo);
      });
      dispatch(actionSetLaborTicketActionLoading(false));
      dispatch(
        actionCanonicalActivityDelete({ activityRef: optimisticActivityRef })
      );
      dispatch(actionSetPendingActivityTypeRef(null));
      throwErrorToast(t(`${t('Could not enter:')} ${pendingActivityTypeName}`));
    },
  });

  useEffect(() => {
    dispatch(actionSetIsChangeActivitiesLoading(loading));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  return async (opts) => {
    if (loading || isChangeActivitiesLoading) {
      return;
    }
    await requestCreateActivity(
      createActivityRequestParams(
        pendingActivityTypeRef || opts.pendingActivityTypeRef
      )
    );
  };
};

export default useChangeActivities;
